<script>
  import { onMount } from "svelte";
  import { setCookie, getCookie, getAllCookies, deleteCookie } from "$lib/utilities/cookies";

  import Banner from "./Banner.svelte";
  import Button from "./Button.svelte";
  import { PRIVACY_MANDATE_REGIONS, REQUIRED_COOKIES } from "$lib/utilities/constants";

  export let gtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID;
  export let gtmDebugParams = import.meta.env.VITE_GTM_DEBUG_PARAMS;
  export let scriptSrc;

  export let cookieBanner;

  const defaultDataRedaction = true;
  const consentTypes = [
    "ad_storage",
    "analytics_storage",
    "personalization_storage",
    "functionality_storage",
    "security_storage"
  ];

  //This truly does not work properly with rest params.
  function gtag() {
    /* eslint-disable-next-line prefer-rest-params */
    window.dataLayer.push(arguments);
  }

  function onAcceptSubmit() {
    setCookie("_of", "accept", 180);
    openCookieBanner = false;
    gtag("consent", "update", buildConsentMap("granted"));
  }

  function onRejectSubmit() {
    setCookie("_of", "reject", 180);
    openCookieBanner = false;
    deleteCookies();
    gtag("consent", "update", buildConsentMap("denied"));
  }

  function deleteCookies() {
    Object.keys(getAllCookies()).forEach((cookie) => {
      if (!REQUIRED_COOKIES.includes(cookie)) {
        deleteCookie(cookie);
      }
    });
  }

  const buildConsentMap = (consentValue) => {
    return consentTypes.reduce((hash, elem) => {
      hash[elem] = consentValue;
      return hash;
    }, {});
  };

  //This component has some complexity meant to support more indepth information
  //to be captured by the tag manager at a later date.
  const getScriptSrcFromInitGtm = () => {
    const getScriptSrcForGtm = (containerId, params) => {
      if (typeof containerId !== "string" || !containerId.length) {
        // eslint-disable-next-line no-console
        console.error("GTM container id variable is not a string or is empty");
        return;
      } else {
        let script = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
        if (params) {
          script += `&${params}`;
        }

        return script;
      }
    };

    try {
      window.dataLayer = window.dataLayer || [];

      let action = getCookie("_of");
      if (action === "accept") {
        gtag("consent", "default", buildConsentMap("granted"));
      } else if (action === "reject") {
        deleteCookies();

        let consentMap = buildConsentMap("denied");
        gtag("consent", "default", consentMap);
      } else {
        deleteCookies();

        //Deny all cookie storage types in regions where this is prohibited.
        let consentMap = buildConsentMap("denied");
        consentMap.region = PRIVACY_MANDATE_REGIONS;
        gtag("consent", "default", consentMap);
      }

      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js"
      });
      gtag("set", "url_passthrough", true);
      gtag("set", "ads_data_redaction", defaultDataRedaction);
      //If other items are needed for GTM later, we can add them here

      openCookieBanner = !action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Google Tag Manager.", error);
    }

    return getScriptSrcForGtm;
  };

  let openCookieBanner = false;

  onMount(() => {
    scriptSrc = getScriptSrcFromInitGtm()(gtmContainerId, gtmDebugParams);
  });
</script>

<svelte:head>
  {#if scriptSrc}
    <script async src={scriptSrc}></script>
  {/if}
</svelte:head>

<!-- Google Tag Manager (noscript) -->
<noscript
  ><iframe
    title="gtm"
    src="https://www.googletagmanager.com/ns.html?id={gtmContainerId}&{gtmDebugParams}"
    height="0"
    width="0"
    style="display:none;visibility:hidden"
  /></noscript
>
<!-- End Google Tag Manager (noscript) -->

{#if cookieBanner}
  <Banner
    variant={cookieBanner.variant}
    isOpen={openCookieBanner}
    title={cookieBanner.headline}
    bodyText={cookieBanner.text}
    additionalInformationLink={cookieBanner.link}
    fullWidth={cookieBanner.fullWidth}
  >
    <Button
      variant="ghost"
      on:click={onRejectSubmit}
      class="w-[141px] h-36 lg:h-[44px]"
      testid="reject-cookies"
    >
      Reject
      <span class="sr-only">Cookies</span>
    </Button>
    <Button
      variant="primary"
      on:click={onAcceptSubmit}
      class="w-[141px] h-36 lg:h-[44px]"
      testid="accept-cookies"
    >
      Accept
      <span class="sr-only">Cookies</span>
    </Button>
  </Banner>
{/if}
